import type { Router } from "@core/Api/Router";
import type { ArticleDiffData } from "@core/SitePresenter/types/ArticlePage";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import FetchService from "@core-ui/ApiServices/FetchService";
import ErrorConfirmService from "@ext/errorHandlers/client/ErrorConfirmService";
import DefaultError from "@ext/errorHandlers/logic/DefaultError";
import BranchUpdaterService from "@ext/git/actions/Branch/BranchUpdaterService/logic/BranchUpdaterService";
import { PublishEmitter } from "@ext/git/actions/Publish/logic/PublishEmitter";
import SyncService from "@ext/git/actions/Sync/logic/SyncService";
import type { ArticleDiffResponse } from "@ext/git/core/Diff/logic/live/ArticleDiffResponse";
import type { TreeReadScope } from "@ext/git/core/GitCommands/model/GitCommandsModel";
import type { DiffItemContentData } from "@ext/git/core/GitDiffItemCreator/DiffItemContent/DiffItemContent";
import t from "@ext/localization/locale/translate";
import { GitTreeScopeParser } from "@ext/versioning/GitTreeScopeParser";
import { FileStatus } from "@ext/Watchers/model/FileStatus";
import type { JSONContent } from "@tiptap/core";
import { create } from "zustand";

export type LiveDiffBaseline = { editTree: JSONContent | null; content?: string };
export type LiveDiffStatus = "loading" | "on" | "error";
export type LiveDiffView = "wysiwyg" | "markdown";

export interface LiveDiffSession {
	id: number;
	key: string;
	catalogName: string;
	articlePath: string;
	scope?: string;
	oldScope: string;
	view: LiveDiffView;
	path: string;
	status: LiveDiffStatus;
	data?: ArticleDiffData;
	baseline?: LiveDiffBaseline | null;
	revision?: DiffItemContentData | null;
	apiUrlCreator: ApiUrlCreator;
	router: Router;
}

interface LiveDiffState {
	session: LiveDiffSession | null;
	generation: number;
	attached: boolean;
}

export const useLiveDiffStore = create<LiveDiffState>()(() => ({ session: null, generation: 0, attached: false }));

const LIVE_STATUSES = new Set<FileStatus>([FileStatus.modified, FileStatus.current, FileStatus.rename]);

export const isLiveDiffStatus = (status: FileStatus | undefined) => !!status && LIVE_STATUSES.has(status);

// Revision links (`scope`) stay on the legacy path: in markdown mode the server serves them before the rights check.
export const isLiveDiffPage = (data: { mode?: string; diff?: ArticleDiffData | null } | undefined) =>
	!!data?.diff &&
	(data.mode === "edit" || data.mode === "markdown") &&
	!data.diff.scope &&
	isLiveDiffStatus(data.diff.sideBarData?.data?.status);

type CacheRecord = {
	data: ArticleDiffData;
	baseline: DiffItemContentData | null;
	revision?: DiffItemContentData | null;
};
const CACHE_LIMIT = 8;
const cache = new Map<string, CacheRecord>();
let workspacePath = "";
let lastSessionId = 0;

const readCache = (key: string) => {
	const record = cache.get(key);
	if (!record) return undefined;
	cache.delete(key);
	cache.set(key, record);
	return record;
};

const writeCache = (key: string, record: CacheRecord) => {
	cache.delete(key);
	cache.set(key, record);
	for (const oldest of cache.keys()) {
		if (cache.size <= CACHE_LIMIT) break;
		cache.delete(oldest);
	}
};

const PARENT = "parent";

const sessionKey = (input: { catalogName: string; articlePath: string; scope?: string; oldScope: string }) =>
	`${workspacePath}|${input.catalogName}|${input.articlePath}|${input.scope ?? ""}|${input.oldScope}`;

const scopeToString = (scope: TreeReadScope | null | undefined): string | undefined =>
	scope ? GitTreeScopeParser.toString(scope) : undefined;

const bump = () => {
	const generation = useLiveDiffStore.getState().generation + 1;
	useLiveDiffStore.setState({ generation });
	return generation;
};

// Router.pushQuery pushes the route it was created on; check window.location or a late response navigates back.
const isRouteCurrent = (router: Router) => {
	if (typeof window === "undefined") return true;
	const trim = (value: string) => value.replace(/\/+$/, "");
	return trim(window.location.pathname).endsWith(trim(router.path));
};

export const setLiveDiffWorkspace = (path: string) => {
	if (path === workspacePath) return;
	workspacePath = path;
	cache.clear();
	bump();
};

export const invalidateLiveDiff = () => {
	cache.clear();
	bump();
	void refreshLiveDiff();
};

let subscribed = false;
const subscribeInvalidation = () => {
	if (subscribed) return;
	subscribed = true;
	PublishEmitter.events.on("finish", invalidateLiveDiff);
	BranchUpdaterService.addListener(invalidateLiveDiff);
	SyncService.events.on("finish", invalidateLiveDiff);
	// Sync `finish` fires before conflict resolution, which has its own event.
	SyncService.events.on("conflict-resolved", invalidateLiveDiff);
};

export interface EnableLiveDiffInput {
	catalogName: string;
	articlePath: string;
	scope?: string;
	oldScope?: string;
	view?: LiveDiffView;
	known?: ArticleDiffData;
	apiUrlCreator: ApiUrlCreator;
	router: Router;
}

type DiffQuery = { diff?: string; oldScope?: string; scope?: string };

const withQuery = (router: Router, query: DiffQuery) => ({ ...(router.query || {}), ...query });

const withDiffQuery = (router: Router, oldScope: string, scope?: string) =>
	withQuery(router, { diff: "1", oldScope, scope });

// Skip unchanged addresses: the one-shot preventNextPushRefresh flag would otherwise swallow the next navigation.
const syncAddress = (router: Router, query: DiffQuery) => {
	const current = router.query || {};
	const same = Object.keys(query).every((name) => (current[name] ?? undefined) === query[name]);
	if (same) return;
	router.setPreventNextPushRefresh(true);
	router.pushQuery(withQuery(router, query));
};

type FetchInput = { apiUrlCreator: ApiUrlCreator; scope?: string; known?: ArticleDiffData };

const fetchRecord = async (input: FetchInput, oldScope: string): Promise<CacheRecord | null> => {
	const { apiUrlCreator, known } = input;
	if (known?.sideBarData) {
		const filePath = known.sideBarData.data.filePath;
		const oldPath = filePath.oldPath ?? filePath.path;
		const res = await FetchService.fetch<DiffItemContentData>(
			apiUrlCreator.getVersionControlDiffItemContentUrl(known.oldScope ?? "HEAD", oldPath, false),
		);
		if (!res.ok) throw new Error(`baseline ${res.status}`);
		return { data: known, baseline: await res.json() };
	}

	const res = await FetchService.fetch<ArticleDiffResponse>(
		apiUrlCreator.getArticleDiffUrl(input.scope, oldScope === PARENT ? undefined : oldScope),
	);
	if (!res.ok) throw new Error(`article diff ${res.status}`);
	const response = await res.json();
	if (!response.sideBarData) return null;
	return {
		data: {
			sideBarData: response.sideBarData,
			scope: response.scope ?? null,
			oldScope: response.oldScope ?? "HEAD",
		},
		baseline: response.baseline,
		revision: response.revision,
	};
};

const isFirstCommit = (record: CacheRecord) => !!record.revision && record.baseline === null;

const lacksTree = (record: CacheRecord, view: LiveDiffView) => {
	if (view === "markdown") return false;
	if (record.revision && !record.revision.editTree) return true;
	return !isFirstCommit(record) && !record.baseline?.editTree;
};

// The open working-copy session must follow HEAD: publish, sync and discard move it under the editor.
const refreshLiveDiff = async () => {
	const { session, generation } = useLiveDiffStore.getState();
	if (session?.status !== "on" || session.revision) return;

	let record: CacheRecord | null = null;
	let failure: { error: unknown } | null = null;
	try {
		record = await fetchRecord(session, session.oldScope);
	} catch (error) {
		failure = { error };
	}
	const state = useLiveDiffStore.getState();
	if (state.session?.id !== session.id || state.generation !== generation) return;

	if (
		failure ||
		!record ||
		!isLiveDiffStatus(record.data.sideBarData?.data.status) ||
		lacksTree(record, session.view)
	) {
		disableLiveDiff(session.router);
		if (failure)
			ErrorConfirmService.notify(new DefaultError(t("diff.live.baseline-error"), failure.error as Error));
		return;
	}
	writeCache(session.key, record);
	useLiveDiffStore.setState({ session: { ...state.session, data: record.data, baseline: record.baseline } });
};

export const enableLiveDiff = async (input: EnableLiveDiffInput) => {
	subscribeInvalidation();
	const view: LiveDiffView = input.view ?? "wysiwyg";
	const oldScope = input.oldScope ?? (input.scope ? PARENT : "HEAD");
	const key = sessionKey({ ...input, oldScope });
	const state = useLiveDiffStore.getState();
	if (state.session?.key === key && state.session.status !== "error") return;

	const id = ++lastSessionId;
	let generation = bump();
	useLiveDiffStore.setState({
		session: {
			id,
			key,
			catalogName: input.catalogName,
			articlePath: input.articlePath,
			scope: input.scope,
			oldScope,
			view,
			path: input.router.path,
			status: "loading",
			apiUrlCreator: input.apiUrlCreator,
			router: input.router,
		},
	});

	let record: CacheRecord | null = null;
	// Invalidation mid-fetch must retry with the new generation, or the session hangs in `loading`.
	for (let attempt = 0; ; attempt++) {
		let failure: { error: unknown } | null = null;
		try {
			record = readCache(key) ?? (await fetchRecord(input, oldScope));
		} catch (error) {
			failure = { error };
		}
		const state = useLiveDiffStore.getState();
		if (state.session?.id !== id || state.session.status !== "loading") return;
		if (state.generation !== generation) {
			if (attempt >= 2) {
				useLiveDiffStore.setState({ session: null });
				return;
			}
			generation = state.generation;
			continue;
		}
		if (failure) {
			// Clear diff params, or a reload would retry enabling.
			useLiveDiffStore.setState({ session: { ...state.session, status: "error" } });
			syncAddress(input.router, { diff: undefined, oldScope: undefined, scope: undefined });
			ErrorConfirmService.notify(new DefaultError(t("diff.live.baseline-error"), failure.error as Error));
			return;
		}
		break;
	}
	if (!isRouteCurrent(input.router)) {
		useLiveDiffStore.setState({ session: null });
		return;
	}

	const status = record?.data.sideBarData?.data.status;
	const resolvedOldScope = record ? (scopeToString(record.data.oldScope) ?? oldScope) : oldScope;
	if (!record || !isLiveDiffStatus(status) || (input.scope && record.revision === null)) {
		useLiveDiffStore.setState({ session: null });
		input.router.pushQuery(withDiffQuery(input.router, resolvedOldScope, input.scope));
		return;
	}
	if (lacksTree(record, view)) {
		useLiveDiffStore.setState({ session: null });
		input.router.pushQuery({ ...withDiffQuery(input.router, resolvedOldScope, input.scope), mode: "markdown" });
		return;
	}

	writeCache(key, record);
	const session = useLiveDiffStore.getState().session;
	if (!session) return;
	useLiveDiffStore.setState({
		session: {
			...session,
			status: "on",
			oldScope: resolvedOldScope,
			data: record.data,
			baseline: record.baseline,
			revision: record.revision,
		},
	});
	syncAddress(input.router, { diff: "1", oldScope: resolvedOldScope, scope: input.scope });
};

export const disableLiveDiff = (router: Router) => {
	bump();
	useLiveDiffStore.setState({ session: null });
	const query = router.query || {};
	if (!query.diff && !query.oldScope && !query.scope) return;
	router.setPreventNextPushRefresh(true);
	router.pushQuery({ ...query, diff: undefined, oldScope: undefined, scope: undefined });
};

export const closeLiveDiffSession = () => {
	bump();
	useLiveDiffStore.setState({ session: null });
};

export const detachLiveDiff = () => {
	bump();
	useLiveDiffStore.setState({ session: null, attached: false });
};

export const attachLiveDiff = () => useLiveDiffStore.setState({ attached: true });

export const isRevisionSession = (session: LiveDiffSession | null) => session?.status === "on" && !!session.revision;

export const useLiveDiffSession = () => useLiveDiffStore((state) => state.session);
export const useLiveDiffAttached = () => useLiveDiffStore((state) => state.attached);

export const resetLiveDiffForTests = () => {
	cache.clear();
	workspacePath = "";
	lastSessionId = 0;
	useLiveDiffStore.setState({ session: null, generation: 0, attached: false });
};

export const parseScope = (scope: string | undefined) => (scope ? GitTreeScopeParser.parse(scope) : undefined);
